import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getConsent,
  saveConsent,
  acceptAllConsent,
  rejectNonEssentialConsent,
} from "../lib/consent";

const CATEGORIES = [
  {
    key: "essential",
    title: "Essential",
    body: "Keep you signed in, protect your account and payments, and prevent fraud. These are required for the website to work — they can't be turned off, but they never track you for advertising.",
    locked: true,
  },
  {
    key: "functional",
    title: "Functional",
    body: "Save your preferences — recent searches, location, favourite businesses, dashboard settings — so you don't have to set them up every visit.",
  },
  {
    key: "analytics",
    title: "Analytics",
    body: "Optional. Anonymous, aggregated usage data (page speed, dead-clicks, drop-off points) so we can spot slow pages, fix confusing flows, and improve mobile responsiveness.",
  },
  {
    key: "marketing",
    title: "Marketing",
    body: "Optional. Only used if we later add ads or retargeting. Currently BookFreely runs no ad tech and doesn't sell or share your personal data.",
  },
];

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const [choices, setChoices] = useState({ essential: true, functional: false, analytics: false, marketing: false });

  useEffect(() => {
    const c = getConsent();
    if (!c) setOpen(true);
    else setChoices({ ...c });
    // Allow footer link "Cookie preferences" to reopen the panel any time
    const reopen = () => { setManage(true); setOpen(true); };
    window.addEventListener("bf-open-cookie-prefs", reopen);
    return () => window.removeEventListener("bf-open-cookie-prefs", reopen);
  }, []);

  if (!open) return null;

  const acceptAll = () => { acceptAllConsent(); setOpen(false); };
  const rejectAll = () => { rejectNonEssentialConsent(); setOpen(false); };
  const savePrefs = () => { saveConsent(choices); setOpen(false); };

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-50 px-3 sm:px-6 pb-3 sm:pb-6 pointer-events-none"
    >
      <div
        className="pointer-events-auto max-w-3xl mx-auto rounded-2xl border shadow-lg overflow-hidden"
        style={{ background: "var(--gf-bg)", borderColor: "var(--gf-border-strong)", boxShadow: "var(--gf-shadow-lifted)" }}
      >
        {!manage ? (
          <div className="p-5 sm:p-6">
            <h3 className="font-display text-lg sm:text-xl" style={{ color: "var(--gf-text)" }}>
              A quick note about cookies
            </h3>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--gf-text-soft)" }}>
              Essential cookies keep you signed in and your account secure. Functional cookies save your preferences. Optional analytics and marketing cookies are off unless you turn them on — and no analytics provider is currently configured, so accepting analytics does not load one today.{" "}
              <Link to="/cookies" className="underline" style={{ color: "var(--gf-text)" }}>Learn more</Link>.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                data-testid="cookie-manage-btn"
                onClick={() => setManage(true)}
                className="rounded-md border px-4 py-2 text-sm order-3 sm:order-1"
                style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
              >
                Manage preferences
              </button>
              <button
                data-testid="cookie-reject-btn"
                onClick={rejectAll}
                className="rounded-md border px-4 py-2 text-sm order-2"
                style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
              >
                Reject non-essential
              </button>
              <button
                data-testid="cookie-accept-btn"
                onClick={acceptAll}
                className="rounded-md px-4 py-2 text-sm font-medium order-1 sm:order-3"
                style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
              >
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <div className="flex items-start justify-between">
              <h3 className="font-display text-lg sm:text-xl" style={{ color: "var(--gf-text)" }}>Cookie preferences</h3>
              <button onClick={() => setManage(false)} className="text-xl leading-none" style={{ color: "var(--gf-text-muted)" }}>×</button>
            </div>
            <p className="mt-1 text-sm" style={{ color: "var(--gf-text-soft)" }}>
              You can change these later from the footer at any time.
            </p>

            <div className="mt-4 space-y-3">
              {CATEGORIES.map((cat) => (
                <label
                  key={cat.key}
                  data-testid={`cookie-cat-${cat.key}`}
                  className="flex items-start gap-3 rounded-lg border p-3"
                  style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
                >
                  <input
                    type="checkbox"
                    data-testid={`cookie-toggle-${cat.key}`}
                    checked={cat.locked ? true : !!choices[cat.key]}
                    disabled={cat.locked}
                    onChange={(e) => setChoices({ ...choices, [cat.key]: e.target.checked })}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium block" style={{ color: "var(--gf-text)" }}>
                      {cat.title}{cat.locked && <span className="ml-2 text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Always on</span>}
                    </span>
                    <span className="text-xs mt-1 block" style={{ color: "var(--gf-text-soft)" }}>{cat.body}</span>
                  </span>
                </label>
              ))}
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                data-testid="cookie-reject-inside-btn"
                onClick={rejectAll}
                className="rounded-md border px-4 py-2 text-sm"
                style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
              >
                Reject non-essential
              </button>
              <button
                data-testid="cookie-save-btn"
                onClick={savePrefs}
                className="rounded-md px-4 py-2 text-sm font-medium"
                style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
